import { JSX, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { List } from '@chakra-ui/react'
import type { Customer } from '@leid/api-customers'
import { DistanceCoordinate, Region } from '@leid/api-locations'

import { RegionWithAvailability } from 'app/components/pages/products/ProductInfoAndRentalSection/components/RentalSection/components/LocationsSelector'
import { ButtonOutlined } from 'components/ui/Buttons/Button'
import apiClient from 'helpers/clients/apiClient'
import { useCustomerInfo } from 'hooks/useCustomerInfo'

import { CustomerSelectedLocation } from './RegionsSectionHeader/customerSelectedLocation'
import { RegionsSectionHeader } from './RegionsSectionHeader'

type Props = {
  title: string
  regions: RegionWithAvailability[]
  selectedRegionId: number | undefined
  onRegionChange: (selectedRegion: Region | undefined) => void
}

export const RegionsList = (props: Props): JSX.Element => {
  const { title, regions, selectedRegionId, onRegionChange } = props

  const { t } = useTranslation(['locations'])
  const { customerInfo } = useCustomerInfo()

  const [listSize] = useState(10)
  const [collapsed, setCollapsed] = useState(false)
  const [regionsToShow, setRegionsToShow] = useState<RegionWithAvailability[]>(regions)
  const [customerLocation, setCustomerLocation] = useState<CustomerSelectedLocation | undefined>(undefined)

  useEffect(() => {
    populateRegions()
  }, [])

  useEffect(() => {
    populateRegions()
  }, [selectedRegionId, customerLocation, customerInfo])

  useEffect(() => {
    setRegions(regionsToShow)
  }, [regions])

  const getDistanceCoordinates = (
    region: Region | undefined,
    customerLocation: CustomerSelectedLocation | undefined,
    customer: Customer | undefined,
  ): DistanceCoordinate | undefined => {
    if (region) {
      return {
        latitudeCoordinate: region.lat,
        longitudeCoordinate: region.long,
      }
    }

    if (customerLocation) {
      return {
        latitudeCoordinate: customerLocation.latitude,
        longitudeCoordinate: customerLocation.longitude,
      }
    }

    if (customer) {
      return {
        latitudeCoordinate: customer?.coordinates?.latitude,
        longitudeCoordinate: customer?.coordinates?.longitude,
      }
    }

    return undefined
  }

  const setRegions = (regions: Region[]) => {
    const regionsWithAvailability = regions.map<RegionWithAvailability>((s) => {
      const result: RegionWithAvailability = { ...s, hasAvailableLocations: undefined }
      const originalRegion = props.regions.find((r) => r.id === s.id)
      if (originalRegion) {
        result.hasAvailableLocations = originalRegion.hasAvailableLocations
      }
      return result
    })

    // already sorted, so here we group them by availability
    const availableRegions = regionsWithAvailability.filter((s) => s.hasAvailableLocations === true)
    const notAvailableRegions = regionsWithAvailability.filter((s) => s.hasAvailableLocations === false)
    const emptyRegions = regionsWithAvailability.filter((s) => s.hasAvailableLocations === undefined)

    const sortedList = [...availableRegions, ...notAvailableRegions, ...emptyRegions]

    if (selectedRegionId) {
      const selectedRegion = sortedList.find((s) => s.id === selectedRegionId)
      const newRegions = sortedList.filter((s) => s.id !== selectedRegionId)
      if (selectedRegion) {
        newRegions.unshift(selectedRegion)
      }
      setRegionsToShow(newRegions)
    } else {
      setRegionsToShow(sortedList)
    }
  }

  const populateRegions = async (): Promise<void> => {
    let region: Region | undefined
    if (selectedRegionId) {
      const response = await apiClient.getRegion(selectedRegionId)
      if (response.success && response.data) {
        region = response.data
      }
    }

    const distanceCoordinates = getDistanceCoordinates(region, customerLocation, customerInfo.customer)

    const response = await apiClient.getRegions({ active: true, allData: true }, undefined, distanceCoordinates)
    if (response.success && response.data) {
      setRegions(response.data.rows)
    }
  }

  const handleChange = (region: Region) => {
    if (selectedRegionId === region.id) {
      onRegionChange(undefined)
      return
    }

    onRegionChange(region)
  }

  const handleCustomerSelectedLocationChange = (selectedLocation: CustomerSelectedLocation) => {
    if (selectedRegionId) {
      onRegionChange(undefined)
    }
    setCustomerLocation(selectedLocation)
  }

  return (
    <>
      <RegionsSectionHeader
        title={title}
        selectedRegionId={selectedRegionId}
        onCustomerLocationSelected={handleCustomerSelectedLocationChange}
      />

      <List.Root width="100%" marginBottom={5} marginTop={5} display={'flex'} flexDirection={'row'} flexWrap={'wrap'}>
        {regionsToShow.map((region, idx) => {
          if (idx < listSize) {
            return (
              <List.Item
                key={region.id}
                onClick={() => handleChange(region)}
                marginRight={5}
                marginBottom={5}
                unselectable="on"
                listStyleType="none"
              >
                <ButtonOutlined
                  key={region.id}
                  isSelected={selectedRegionId && selectedRegionId === region.id ? true : false}
                  isDimmed={region.hasAvailableLocations === false}
                  isOff={region.hasAvailableLocations === undefined}
                >
                  {region.name}
                </ButtonOutlined>
              </List.Item>
            )
          }

          const result = (
            <List.Item
              key={region.id}
              onClick={() => handleChange(region)}
              hidden={!collapsed}
              marginRight={5}
              marginBottom={5}
              listStyleType="none"
            >
              <ButtonOutlined
                key={region.id}
                isDimmed={region.hasAvailableLocations === false}
                isOff={region.hasAvailableLocations === undefined}
              >
                {region.name}
              </ButtonOutlined>
            </List.Item>
          )

          if (idx === regions.length - 1) {
            return (
              <>
                {result}
                <List.Item
                  key="divider"
                  onClick={() => setCollapsed(!collapsed)}
                  marginRight={5}
                  marginBottom={5}
                  listStyleType="none"
                >
                  <ButtonOutlined key="divider" noBorder={true}>
                    {collapsed ? t('less') : t('more')}
                  </ButtonOutlined>
                </List.Item>
              </>
            )
          }

          return result
        })}
      </List.Root>
    </>
  )
}
